export enum PATHS {
  HOME = "/",
  AUTH = "/auth",
  LOGIN = "/login/:productId?/:priceId?/:seat?",
  SIGNUP = "/signup/:productId?/:priceId?/:seat?",
  // P4-F4: the five one-time-link pages take the token from `#token=` (new mails) or this optional path segment (links
  // mailed before) — see utils/linkToken. The tokenless URL renders the same route.
  AUTH_MAGIC = "/auth/magic/:token?",
  // Admin "Login as User" impersonation landing — backoffice redirects here with a
  // short-lived login JWT in the query string (?token=<JWT>). Public route. REF-5759.
  AUTH_IMPERSONATE = "/auth/impersonate",
  CANCEL_PLAN = "/cancel-plan",
  ACADEMY_HOME = "/academy",
  ACADEMY_ALL_COURSES = "/academy/all-courses",
  ACADEMY_COURSE = "/academy/course/:id",
  ACADEMY_COURSE_WATCH = "/academy/course/:id/watch",
  // Public (no-login) certificate verify landing — the human destination behind the
  // LinkedIn share link. The crawler-facing OG page is served by the backend. REF-5778.
  ACADEMY_CERTIFICATE_VERIFY = "/academy/verify/:certificateId",
  // Clean top-level subtype "showcase" routes (no /assets prefix): full-grid asset views with a
  // "<Root> / <Type ▾>" breadcrumb and no in-page sidebar. Rendered by MyAssetsPage, which reads the
  // pathname + :type segment. Design Assets → fabric | print | other; Photoshoots Assets →
  // products | backgrounds | poses.
  DESIGN_ASSETS = "/design-assets",
  DESIGN_ASSETS_TYPE = "/design-assets/:type",
  PHOTOSHOOT_ASSETS = "/photoshoot-assets",
  PHOTOSHOOT_ASSETS_TYPE = "/photoshoot-assets/:type",
  PHOTOSHOOT_ASSETS_MODELS = "/photoshoot-assets/models",
  // REF-6157 — the moodboards-style virtual-studio lifestyle-background generation page (full-viewport, no
  // layout chrome). Declared BEFORE PHOTOSHOOT_ASSETS_TYPE so "/photoshoot-assets/backgrounds/lifestyle"
  // isn't swallowed by ":type". Separate from the flat AddBackgroundModal upload flow.
  PHOTOSHOOT_ASSETS_BACKGROUNDS_LIFESTYLE = "/photoshoot-assets/backgrounds/lifestyle",
  TREND_ANALYSIS = "/trend-analysis",
  TREND_HISTORY = "/trend-history",
  // Trend History told as Insights (T-Fashion's way), built beside /trend-history; it takes its place when ready.
  // Runway Trends (was /trend-history-v2 until 2026-10-24; the old paths redirect, Router.tsx).
  TREND_HISTORY_V2 = "/runway-trends",
  TREND_HISTORY_V2_SEASON = "/runway-trends/:season/:gender",
  TREND_HISTORY_V2_CITY = "/runway-trends/:season/:gender/:city",
  // Retail Trends (REF-7160): Runway Trends' finish over the brand catalogue's arrivals — its months on the list, a
  // window's analysis at `/:period/:anchor` (`month/2026-09-01`), brand · gender · category in the query.
  RETAIL_TRENDS = "/retail-trends",
  RETAIL_TRENDS_WINDOW = "/retail-trends/:period/:anchor",
  // Retail Data (2026-10-28): Runway Data's map and stories on retail brands' new products, a month for a season
  // (pages/TrendDataPage/trendSource.ts). `?season=` is the month, `YYYY-MM`.
  RETAIL_DATA = "/retail-data",
  RETAIL_DATA_GROUP = "/retail-data/:group",
  RETAIL_DATA_TYPE = "/retail-data/:group/:type",
  REPORT = "/report/:id",
  // Reports shared with the user's team (REF-7271). Not the moodboard analysis above (`/report/:id`).
  REPORTS = "/reports",
  REPORTS_HM_SUPPLIER_LIST = "/reports/hm-supplier-list",
  BRAND_DNA_REPORT = "/brand-dna-report/:id",
  MODEL_DETAIL = "/model/:id",
  FABRIC_DNA_DETAIL = "/fabric-dna/:id",

  PHOTOSHOOT = "/photoshoot/:id?",
  PHOTOSHOOTS = "/photoshoots",
  PHOTOSHOOTS_STUDIO = "/photoshoots/studio",
  // Studio-based Ghost projects are listed + opened through the studio routes above (their ids resolve on
  // the studio getProject endpoint); they have no dedicated detail route.
  PHOTOSHOOTS_STUDIO_DETAIL = "/photoshoots/studio/:id",
  PHOTOSHOOT_STUDIO = "/photoshoot-studio",
  TECH_PACK_LANDING = "/tech-pack-landing",
  TECH_PACK = "/tech-pack/:id?",
  TECH_PACKS = "/tech-packs",
  FIT_MODELS = "/fit-models/:id?",
  SVG_EDITOR = "/svg-editor",
  MARKETPLACE_STUDIO = "/marketplace-studio",
  BULK_PROCESS = "/bulk-process",
  BULK_PROCESS_CONVERT = "/bulk-process/convert",

  DESIGN = "/design",
  // Renamed from /trends; the page is "Moodboards" now. /trends is kept alive
  // as a redirect for any deep links / external references still in the wild.
  MOODBOARDS = "/moodboards",
  // Deep-link routes for the Moodboards tabs (left-menu sub-items). TrendsPage reads the
  // pathname to preselect the matching tab. Static (not a `:tab` param) so they never
  // shadow /moodboards/new (CREATE_MOODBOARD).
  MOODBOARDS_MY = "/moodboards/my",
  MOODBOARDS_RUNWAY = "/moodboards/runway",
  MOODBOARDS_BRAND_CATALOGUES = "/moodboards/brand-catalogues",
  MOODBOARDS_SHARED = "/moodboards/shared",
  // One board, one URL (REF-6484). The detail used to be pure component state, so a finished
  // moodboard had no address to send anyone to — the completion email and the bell notification
  // both had to fall back to `/report/:id`, the legacy A4 report page nothing in the app links to.
  // Each tab gets its own `:id` child rather than one `/moodboards/:id`, because the segment names
  // WHICH LIST the board lives in and that is what tells the detail which endpoint serves it
  // (`utils/moodboardSource`): my/shared → trends/report, runway → brand-dna/report,
  // brand-catalogues → the catalogue's own. A single `/moodboards/:id` would also shadow
  // /moodboards/new and the tab words themselves.
  MOODBOARDS_MY_DETAIL = "/moodboards/my/:id",
  MOODBOARDS_RUNWAY_DETAIL = "/moodboards/runway/:id",
  MOODBOARDS_BRAND_CATALOGUES_DETAIL = "/moodboards/brand-catalogues/:id",
  MOODBOARDS_SHARED_DETAIL = "/moodboards/shared/:id",
  BRAND_DNAS = "/brand-dnas",
  FABRIC_TRAININGS = "/design-assets/fabric-trainings",
  // Standalone Design Assets sub-page for the user's saved color palettes (api/color-palettes) — a
  // dedicated static route alongside FABRIC_TRAININGS, not swallowed by the DESIGN_ASSETS_TYPE
  // catch-all (REF-6361).
  DESIGN_ASSETS_COLOR_PALETTES = "/colors",
  // Standalone Design Assets sub-page for the user's saved brand kits — declared standalone (like
  // DESIGN_ASSETS_COLOR_PALETTES above) so it isn't swallowed by the DESIGN_ASSETS_TYPE catch-all.
  // REF-6155-brand-kits — see src/pages/BrandKitsPage/README.md.
  DESIGN_ASSETS_BRAND_KITS = "/brand-kits",
  // Standalone Browzwear boards page, reached from the My Assets left-menu section (directly
  // under Photoshoot Assets). Recovered from the retired /assets tabbed page's Integrations tab.
  INTEGRATIONS = "/integrations",
  // REF-6702 — OAuth return page for the Miro connection (code/state/error in the query string).
  MIRO_CALLBACK = "/integrations/miro/callback",
  WHATS_NEW = "/whats-new",
  NOTIFICATIONS = "/notifications",
  // REF-5966 — the single unified create-moodboard page (source panel + category sections).
  CREATE_MOODBOARD = "/moodboards/new",
  // REF-6155-brand-kits — Create-Brand-Kit page, cloned from CREATE_MOODBOARD (Assets-only
  // source panel, stubbed submit — see src/pages/CreateBrandKitAddPage). Static (not swallowed
  // by DESIGN_ASSETS_BRAND_KITS since react-router v6 matches exact paths).
  CREATE_BRAND_KIT = "/brand-kits/new",
  // REF-6155-brand-kits — Brand Kit detail/edit screen, same CreateBrandKitAddPage component in
  // `isEdit` mode. v6 ranks the static "/brand-kits/new" above this dynamic "/brand-kits/:id", so
  // CREATE_BRAND_KIT above stays reachable and create mode is unaffected.
  BRAND_KIT_DETAIL = "/brand-kits/:id",
  NEW_TRAINING = "/new-training/:id?",
  STYLE_TRAINING = "/new-training/style/:id?",
  FABRIC_TRAINING = "/new-training/fabric/:id?",
  MODEL_TRAINING = "/new-training/model/:id?",
  DISCOVER = "/discover",
  // Left-menu deep links into the Explore Trends sources. DiscoverPage reads the pathname to
  // preselect the Runway vs Social Media (fashion-media) vs Retail Brands (REF-6762) source.
  DISCOVER_RUNWAY = "/discover/runway",
  // Their own pages in the menu since 2026-10-24 (were /discover/social-media and /discover/retail-brands; those redirect).
  DISCOVER_SOCIAL = "/social-media",
  // Retail Brands' old page (retired 2026-10-28, redirects to Retail Trends); the catalogue feed still reads it as its source.
  DISCOVER_RETAIL_BRANDS = "/retail-brands",
  // Trends History: the trend categories' clusters as a draggable wall, each opening its data story. MOCK data for now.
  // Runway Data (was /discover/history until 2026-10-24; that redirects with its query).
  TRENDS_HISTORY = "/runway-data",
  // A "Selected by Refabric" collection opened full page (its looks as a draggable wall).
  DISCOVER_COLLECTION = "/discover/collection/:designer",
  // A trend category's data (the home's category stacks open it). MOCK data for now.
  TREND_DATA = "/trends/data/:group",
  // A type of a trend category, opened as a canvas of its own (its deep dive).
  TREND_DATA_TYPE = "/trends/data/:group/:type",
  ACCOUNT = "/account",
  Shared = "/shared",
  EXPLORE = "/explore",
  EXPLORE_NEWS = "/explore/news",
  EXPLORE_TUTORIALS = "/explore/tutorials",
  RESET_PASSWORD = "/reset-password/:token?",
  EMAIL_VERIFY = "/email-verify/:token?/:productId?/:priceId?",
  PAYMENT_SUCCESS = "/payment/success/:orderId",
  PAYMENT_FAIL = "/payment/error/:orderId",
  CHECKOUT = "/checkout/:productId/:priceId/:seat?",
  GUEST_SUCCESS = "/guest/success/:stateId",
  SET_PASSWORD = "/set-password",
  PRICING = "/pricing",
  PRIVACY_POLICY = "/privacy-policy",
  TERMS_OF_USE = "/terms-of-use",
  ACCEPT_INVITE = "/accept_invite/:token?",
  ACCEPT_MEMBER_INVITE = "/accept_member_invite/:token?",

  CREATE = "/create",
  // "History" left-menu item — full-grid Created view (generated content) with a
  // "History / <Type ▾>" breadcrumb. MyAssetsPage reads the :type segment to preselect the subtype.
  HISTORY = "/history",
  HISTORY_TYPE = "/history/:type",
  FAVORITES = "/favorites",
  POST = "/posts/:id",
  Promo = "/promo/:token",
  TEST = "/test-color",

  MAINTENANCE = "/maintenance",
  DESIGN_STUDIO = "/design-canvas",
  DESIGN_CANVAS_AGENT = "/design-canvas-agent",
  /** The agent chat's components in every state, on sample data — a design surface, linked from nowhere. */
  DESIGN_CANVAS_AGENT_KIT = "/design-canvas-agent/kit",
  /** Generative UI: the agent's cards being redesigned, on sample data — a design surface, linked from nowhere. */
  DESIGN_CANVAS_AGENT_GEN_UI = "/design-canvas-agent/gen-ui",
  /** Generative UI in motion: the recipe plan running step by step, on sample data — linked from nowhere. */
  DESIGN_CANVAS_AGENT_GEN_UI_2 = "/design-canvas-agent/gen-ui-2",
  /** A clickable chat prototype of the gen-ui cards (approve → the plan runs on the composer) — linked from nowhere. */
  DESIGN_CANVAS_AGENT_MOCK = "/design-canvas-agent-mock",
  /** The design agent's home: the composer on top, the latest trends under it — beside the main menu. */
  DESIGN_CANVAS_AGENT_HOME = "/design-canvas-agent-home",
  CANVAS_PROJECTS = "/canvas-projects",
  COLLECTION_RANGE_PLANS = "/collection-range-plans",
  COLLECTION_RANGE_PLAN_CREATE = "/collection-range-plans/new",
  // Cross-feature "All Projects" list (canvas + studio + techpack), reached from
  // Home's Recent Projects → See All. Backed by GET /v1/recent-projects (REF-5783 service).
  ALL_PROJECTS = "/projects",
  // The Developers section (design §6): API keys, webhooks, jobs, usage … — one route, its screens
  // routed inside the page (`pages/DevelopersPage/panelRegistry.ts` is the screen list).
  DEVELOPERS = "/developers",
  DEVELOPERS_ALL = "/developers/*",
}
